import { useEffect, useRef, useState } from 'react'
import { cn } from '#app/shared/lib/misc.js'

type Props = {
  title: string
  value: string
  className?: string
  unit: string
  min: number
  max: number
  delay: number
}

export default function CircleSpec({
  title,
  unit,
  value,
  className,
  min = 0,
  max = 100,
  delay = 0,
}: Props) {
  const [fillPercentage, setFillPercentage] = useState(0)
  const [inView, setInView] = useState(false)
  const circleRef = useRef(null)

  useEffect(() => {
    const numericValue = Number(value)
    const range = max - min
    const step = range / 6

    let stepIndex
    if (numericValue >= max) {
      stepIndex = 6
    } else {
      stepIndex = Math.ceil((numericValue - min) / step)
    }

    const clampedValue = stepIndex === 6 ? numericValue : min + step * stepIndex
    const percentage = ((clampedValue - min) / range) * 100
    const clampedPercentage = Math.min(Math.max(percentage, 10), 90) // max 90%, min 10%
    if (inView) {
      setTimeout(() => setFillPercentage(clampedPercentage), delay)
    }
  }, [value, min, max, inView])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true)
        } else {
          setInView(false)
        }
      },
      { threshold: 1 },
    )

    if (circleRef.current) {
      observer.observe(circleRef.current)
    }

    return () => {
      if (circleRef.current) {
        observer.unobserve(circleRef.current)
      }
    }
  }, [])

  return (
    <div
      ref={circleRef}
      className={cn(
        `
          flex h-[204px] w-full flex-col items-center gap-[8px] bg-white
          pb-[18px] pt-[20px]

          lg:h-[204px] lg:w-[206px]
        `,
        className,
      )}
    >
      <p className="pb-[6px] text-subtitle2-semibold text-neutral-500">
        {title}
      </p>

      <div
        className={`
          relative flex h-[114px] w-[114px] items-center justify-center
          rounded-full bg-white
        `}
      >
        <svg className="absolute left-0 top-0 h-full w-full">
          <circle
            cx="50%"
            cy="50%"
            r="45%"
            stroke="currentColor"
            strokeWidth="10px"
            className="text-[#D9F2F3]"
            fill="none"
            strokeDasharray="325"
          />
          <circle
            cx="50%"
            cy="50%"
            r="45%"
            stroke="currentColor"
            strokeWidth="10px"
            fill="none"
            strokeDasharray="325"
            strokeDashoffset={325 - (fillPercentage / 100) * 325}
            className="text-primary-500 transition-all duration-500 ease-out"
            transform="rotate(-90 57 57)"
          />
        </svg>
        <div
          className={`
            flex h-[86.2px] w-[86.2px] flex-col items-center justify-center
            gap-3 rounded-full border-[3px] border-[rgba(1,168,178,0.21)]
          `}
        >
          <p className="text-h5-bold text-neutral-900">
            {value}
            {title == 'CONSUMO MEDIO' ? 'l' : ''}
          </p>

          <p className="text-subtitle2-semibold text-neutral-500">{unit}</p>
        </div>
      </div>
      <p className="text-subtitle2-semibold text-neutral-200">
        {delay == 0 ? 'Más' : 'Menos'} es mejor
      </p>
    </div>
  )
}
